// storefront/assets/weebot-compare-engine.js
// Moteur du comparateur Weebot. Helpers purs (testés Node) + rendu DOM (Phase 3).

// ---- helpers purs ----
export function parsePParam(search) {
  const m = /[?&]p=([^&]+)/.exec(search || '');
  if (!m) return [];
  return decodeURIComponent(m[1]).split(',').map(s => s.trim()).filter(Boolean);
}

export function eurosFromCents(c) {
  return Math.round(c) / 100;
}

// direction de victoire par dimension : +1 = max gagne, -1 = min gagne
export const DIMENSIONS = [
  { key: 'price', dir: -1, source: 'price' },
  { key: 'speed_kmh', dir: +1, source: 'specs' },
  { key: 'range_km', dir: +1, source: 'specs' },
  { key: 'motor_w', dir: +1, source: 'specs' },
  { key: 'battery_wh', dir: +1, source: 'specs' },
  { key: 'weight_kg', dir: -1, source: 'specs' },
  { key: 'ip', dir: +1, source: 'specs' },
];

function valueOf(model, dim) {
  const v = dim.source === 'price' ? model.price : (model.specs || {})[dim.key];
  return (v === null || v === undefined || v === '') ? null : Number(v);
}

export function computeWinners(models) {
  const out = {};
  if (models.length < 2) return out;   // 1 seul modèle → rien à comparer (spec §6)
  for (const dim of DIMENSIONS) {
    let best = null, bestHandle = null, tie = false;
    for (const m of models) {
      const v = valueOf(m, dim);
      if (v === null || Number.isNaN(v)) continue;
      if (best === null || v * dim.dir > best * dim.dir) {
        best = v; bestHandle = m.handle; tie = false;
      } else if (v === best) {
        tie = true;
      }
    }
    out[dim.key] = tie ? null : bestHandle;
  }
  return out;
}

export function filterCandidates(index, { segment = null, budgetMin = null, budgetMax = null, query = '' } = {}) {
  const q = (query || '').toLowerCase().trim();
  return index.filter(m => {
    if (segment && !(m.segments || []).includes(segment)) return false;
    if (budgetMax != null && m.price != null && m.price > budgetMax) return false;
    if (budgetMin != null && m.price != null && m.price < budgetMin) return false;
    if (q) {
      const hay = `${m.brand} ${m.model}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function defaultModelsForSegment(index, segment, n = 3) {
  return index
    .filter(m => (m.segments || []).includes(segment))
    .filter(m => m.best_seller_rank != null)
    .sort((a, b) => a.best_seller_rank - b.best_seller_rank)
    .slice(0, n)
    .map(m => m.handle);
}

// ---- live (prix/stock) ----
export async function fetchLive(handle) {
  try {
    const r = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) return null;
    const j = await r.json();
    let featured = j.featured_image || (j.images && j.images[0]) || null;
    if (featured && featured.startsWith('//')) featured = 'https:' + featured;  // Shopify renvoie du protocole-relatif
    return { price: eurosFromCents(j.price), available: !!j.available, title: j.title,
             url: j.url, featured };
  } catch { return null; }
}

// ---- rendu DOM ----
const MAX_MODELS = (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width:780px)').matches) ? 3 : 4;

const SEGMENTS = [
  { key: 'ville', label: 'Ville / légère', icon: 'i-city' },
  { key: 'polyvalente', label: 'Polyvalente', icon: 'i-bolt' },
  { key: 'performance', label: 'Performance', icon: 'i-gauge' },
  { key: 'tout-terrain', label: 'Tout-terrain', icon: 'i-mountain' },
];

const BUDGETS = [
  { label: 'Tous les prix', max: null },
  { label: '< 500 €', max: 500 },
  { label: '500–1000 €', min: 500, max: 1000 },
  { label: '1000–2000 €', min: 1000, max: 2000 },
  { label: '2000 € +', min: 2000, max: null },
];

const LABELS_DEFAULT = [
  ['price', 'Prix', null], ['speed_kmh', 'Vitesse max', 'sur voie privée'],
  ['range_km', 'Autonomie', null], ['motor_w', 'Moteur', null],
  ['battery_wh', 'Batterie', null], ['weight_kg', 'Poids', null], ['ip', 'Étanchéité', null],
];
// lignes du tableau : celles déclarées par l'index (`labels`) s'il en fournit, sinon celles par défaut
let LABELS = LABELS_DEFAULT;
let DISPLAY_KEY = displayKeyOf(LABELS_DEFAULT);
function displayKeyOf(labels) {
  return Object.fromEntries(labels.filter(l => l[0] !== 'price').map(l => [l[0], l[1]]));
}
function applyIndexLabels(data) {
  const l = data && !Array.isArray(data) && Array.isArray(data.labels) ? data.labels : null;
  LABELS = l || LABELS_DEFAULT;
  DISPLAY_KEY = displayKeyOf(LABELS);
}
const BADGE = { price: 'Meilleur prix', speed_kmh: 'Le plus rapide',
                range_km: 'Meilleure autonomie', motor_w: 'Meilleur moteur', weight_kg: 'Le plus compact' };

export function formatEuro(p) {
  if (p == null) return '—';
  const s = Number.isInteger(p) ? String(p) : p.toFixed(2);
  return `${s.replace('.', ',')} €`;
}

function cellText(model, key) {
  if (key === 'price') return formatEuro(model.price);
  const d = (model.specs_display || {})[DISPLAY_KEY[key]];
  return d && d.value ? d.value : '—';
}

const esc = s => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const useSvg = (id, cls) => `<svg${cls ? ` class="${cls}"` : ''}><use href="#${id}"/></svg>`;

const ICON_DEFS = `<svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
<symbol id="i-city" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V7l5-3v17M14 21V10l5-2.5V21M8 9h0M8 13h0M8 17h0"/></symbol>
<symbol id="i-bolt" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3 5 13h6l-1 8 8-11h-6l1-7z"/></symbol>
<symbol id="i-gauge" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19a8 8 0 1 1 16 0"/><path d="M12 19l4-5"/><circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none"/></symbol>
<symbol id="i-mountain" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20l6-11 3.5 6L15 11l6 9z"/></symbol>
<symbol id="i-search" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.2-4.2"/></symbol>
<symbol id="i-link" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 14.5l5-5"/><path d="M11 6.5l1-1a4 4 0 0 1 5.7 5.7l-1 1"/><path d="M13 17.5l-1 1A4 4 0 0 1 6.3 12.8l1-1"/></symbol>
<symbol id="i-trophy" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4h10v4a5 5 0 0 1-10 0V4z"/><path d="M7 6H4.5v1A3 3 0 0 0 7 10M17 6h2.5v1A3 3 0 0 1 17 10M9.5 14.5 9 18h6l-.5-3.5M8 21h8"/></symbol>
<symbol id="i-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.2 4.3L19 7"/></symbol>
<symbol id="i-plus" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></symbol>
<symbol id="i-arrows" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 8l-4 4 4 4M17 8l4 4-4 4M3 12h18"/></symbol>
</defs></svg>`;

function badgesFor(handle, winners, model) {
  const out = [];
  // Badge éditorial porté par l'index (champ "pick") : le modèle que la maison recommande,
  // affiché en premier, avant les critères mesurés.
  if (model && model.pick) out.push(`<span class="wb-cmp-badge wb-cmp-badge-pick">${useSvg('i-trophy')} ${esc(model.pick)}</span>`);
  for (const key of Object.keys(BADGE)) {
    if (winners[key] === handle) {
      out.push(`<span class="wb-cmp-badge wb-cmp-badge-win">${useSvg('i-trophy')} ${esc(BADGE[key])}</span>`);
    }
  }
  if (!out.length) out.push(`<span class="wb-cmp-badge wb-cmp-badge-neu">Le polyvalent</span>`);
  return out.join('');
}

function sized(url, w) {
  if (!url || /[?&]width=/.test(url)) return url;
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
}

function pheadHTML(model, winners) {
  const handle = model.handle;
  const img = sized(model.featured || model.image || '', 320);
  const url = model.url || `/products/${handle}`;
  const stock = model.available !== false
    ? `<div class="wb-cmp-stock"><span class="wb-cmp-dot"></span> En stock</div>`
    : `<div class="wb-cmp-stock wb-cmp-stock-out"><span class="wb-cmp-dot"></span> Indisponible</div>`;
  return `<div class="wb-cmp-phead" data-handle="${esc(handle)}">
    <span class="wb-cmp-rm" data-rm="${esc(handle)}" role="button" aria-label="Retirer ${esc(model.brand)} ${esc(model.model)}">✕</span>
    <div class="wb-cmp-badges">${badgesFor(handle, winners, model)}</div>
    ${img ? `<img src="${esc(img)}" alt="${esc(model.brand)} ${esc(model.model)}" loading="lazy" style="opacity:0;transition:opacity .2s" onload="this.style.opacity=1" onerror="this.style.opacity=0">` : ''}
    <div class="wb-cmp-brand">${esc(model.brand)}</div>
    <div class="wb-cmp-pname">${esc(model.model)}</div>
    <div class="wb-cmp-price">${esc(cellText(model, 'price'))}</div>
    ${stock}
    <a class="wb-cmp-cta" href="${esc(url)}">Voir la fiche →</a>
  </div>`;
}

function addSlotHTML() {
  return `<div class="wb-cmp-addslot" data-add-panel>
    <div class="wb-cmp-plus">${useSvg('i-plus')}</div>
    <b>Ajouter un modèle</b>
    <div class="wb-cmp-search" data-add-trigger>${useSvg('i-search')} Rechercher…</div>
    <input class="wb-cmp-search-input" type="text" placeholder="Marque ou modèle…" data-add-input hidden>
    <ul class="wb-cmp-results" data-add-results hidden></ul>
  </div>`;
}

function valCellHTML(model, key, winners) {
  const isWin = winners[key] === model.handle;
  const cls = ['wb-cmp-cell', 'wb-cmp-val'];
  if (key === 'price') cls.push('wb-cmp-price-row');
  if (isWin) cls.push('wb-cmp-win');
  const tag = isWin
    ? `<span class="wb-cmp-wtag">${useSvg('i-check')} ${esc(key === 'price' ? 'meilleur prix' : key === 'weight_kg' ? 'le plus compact' : 'le meilleur')}</span>`
    : '';
  return `<div class="${cls.join(' ')}">${esc(cellText(model, key))}${tag}</div>`;
}

export function renderTable(root, models, winners) {
  const n = models.length;
  const showAdd = n < MAX_MODELS;

  // header row
  let html = `<div class="wb-cmp-cell wb-cmp-lbl wb-cmp-lblhead"></div>`;
  html += models.map(m => pheadHTML(m, winners)).join('');
  if (showAdd) html += addSlotHTML();

  // spec rows
  for (const [key, label, note] of LABELS) {
    if (!models.some(m => cellText(m, key) !== '—')) continue;   // ligne sans aucune donnee -> masquee (ex: Etancheite pour les velos)
    const small = note ? ` <small>${esc(note)}</small>` : '';
    html += `<div class="wb-cmp-cell wb-cmp-lbl">${esc(label)}${small}</div>`;
    html += models.map(m => valCellHTML(m, key, winners)).join('');
    if (showAdd) html += `<div class="wb-cmp-cell wb-cmp-spacer"></div>`;
  }

  const grid = root.querySelector('.wb-cmp-grid');
  // largeurs pilotées par variables CSS (le @media mobile les redéfinit : libellé fin,
  // colonnes à largeur mini, slot d'ajout à 0). Évite le conflit inline-style vs CSS.
  // n=0 : pas de repeat(0,…) (CSS invalide) → juste libellé + slot d'ajout.
  const prod = n > 0 ? ` repeat(${n}, var(--wb-cmp-colw,1fr))` : '';
  const cols = `var(--wb-cmp-lblw,172px)${prod}${showAdd ? ' var(--wb-cmp-addw,1fr)' : ''}`;
  grid.style.gridTemplateColumns = cols;
  grid.innerHTML = html;
}

function shareUrl(models) {
  const handles = models.map(m => m.handle).join(',');
  return `wee-bot.com/comparateur?p=${handles}`;
}

// ---- shell + état + init ----
function buildShell(root, withSegments) {
  // Le titre de la page (kick, H1, sous-titre) est rendu en Liquid par snippets/weebot-comparateur.liquid.
  const head = withSegments ? `
    <div class="wb-cmp-segbar" data-segbar>
      ${SEGMENTS.map(s => `<div class="wb-cmp-chip" data-seg="${s.key}">${useSvg(s.icon, 'wb-cmp-ico')} ${esc(s.label)}</div>`).join('')}
      <div class="wb-cmp-budget">Budget&nbsp; <select data-budget>${BUDGETS.map((b, i) => `<option value="${i}">${esc(b.label)}</option>`).join('')}</select></div>
    </div>` : '';
  root.innerHTML = `${ICON_DEFS}
  <div class="wb-cmp-wrap">${head}
    <p class="wb-cmp-seg-note" data-seg-note></p>
    <div class="wb-cmp-swipe">${useSvg('i-arrows')} glissez horizontalement</div>
    <div class="wb-cmp-ctable">
      <div class="wb-cmp-scroller"><div class="wb-cmp-grid"></div></div>
    </div>
    <div class="wb-cmp-addbar" data-add-panel>
      <span class="wb-cmp-addbtn" data-add-trigger>${useSvg('i-plus')} Ajouter un modèle à comparer</span>
      <input class="wb-cmp-search-input" type="text" placeholder="Marque ou modèle…" data-add-input hidden>
      <ul class="wb-cmp-results" data-add-results hidden></ul>
    </div>
  </div>`;
}

export async function mountComparison(root, opts = {}) {
  const { index = null, indexUrl = null, segments = true, initialHandles = null } = opts;
  const state = { index: [], models: [], segment: 'ville', budgetIdx: 0 };

  buildShell(root, segments);

  const segNote = root.querySelector('[data-seg-note]');

  const byHandle = h => state.index.find(m => m.handle === h) || null;

  function resolveInstant(handles) {
    return handles.map(byHandle).filter(Boolean).slice(0, MAX_MODELS).map(e => ({ ...e, specs: { ...e.specs } }));
  }
  async function applyLive() {
    const todo = state.models.filter(m => !m._live);
    if (!todo.length) return;
    const live = await Promise.all(todo.map(m => fetchLive(m.handle)));
    todo.forEach((m, i) => {
      m._live = true;
      const l = live[i];
      if (l) { m.price = l.price; m.available = l.available; if (l.url) m.url = l.url; if (l.featured) m.featured = l.featured; if (l.title) m.title = l.title; }
    });
    paint();
  }

  function paint() {
    const winners = computeWinners(state.models);
    renderTable(root, state.models, winners);

    const seg = SEGMENTS.find(s => s.key === state.segment);
    root.querySelectorAll('[data-seg]').forEach(el => {
      el.classList.toggle('wb-cmp-on', el.dataset.seg === state.segment);
    });
    const n = state.models.length;
    if (!segments) {
      segNote.innerHTML = `${n} modèle${n > 1 ? 's' : ''} à comparer`;
    } else {
      segNote.innerHTML = n === 0
        ? `Aucun modèle dans <b>${esc(seg ? seg.label : '')}</b> à ce budget — élargissez le budget ou changez de segment.`
        : `${n} modèle${n > 1 ? 's' : ''} · segment <b>${esc(seg ? seg.label : '')}</b>. Changez de segment, retirez ou ajoutez un modèle.`;
    }

    const addbar = root.querySelector('.wb-cmp-addbar');
    if (addbar) addbar.style.display = n < MAX_MODELS ? '' : 'none';
    root.classList.toggle('wb-cmp-scrollable', n > 2);   // hint mobile « glissez » si >2 colonnes
    root.classList.toggle('wb-cmp-empty', n === 0);      // état vide (filtre sans résultat)

    wireRemove();
    wireAdd();
  }

  function wireRemove() {
    root.querySelectorAll('[data-rm]').forEach(btn => {
      btn.onclick = () => {
        const h = btn.dataset.rm;
        state.models = state.models.filter(m => m.handle !== h);
        paint();
      };
    });
  }

  function wireAdd() {
    const budget = BUDGETS[state.budgetIdx] || {};
    const renderResults = (results, q) => {
      const used = new Set(state.models.map(m => m.handle));
      // Recherche GLOBALE (tous segments) — on doit pouvoir comparer une ville-légère avec
      // une polyvalente. Les segments amorcent le départ chaud, ils n'enferment pas la recherche.
      const cand = filterCandidates(state.index, {
        budgetMin: budget.min, budgetMax: budget.max, query: q,
      }).filter(m => !used.has(m.handle))
        .sort((a, b) => (a.best_seller_rank ?? 9e9) - (b.best_seller_rank ?? 9e9))
        .slice(0, 12);
      results.innerHTML = cand.map(m =>
        `<li data-pick="${esc(m.handle)}">${esc(m.brand)} ${esc(m.model)}<small>${esc((m.specs_display && m.specs_display['Vitesse max'] && m.specs_display['Vitesse max'].value) || '')}</small></li>`
      ).join('') || `<li style="cursor:default;color:#9aa3b2">Aucun résultat</li>`;
      results.querySelectorAll('[data-pick]').forEach(li => {
        li.onclick = () => {
          if (state.models.length >= MAX_MODELS) return;
          const added = resolveInstant([li.dataset.pick]);
          if (added[0]) { state.models = [...state.models, added[0]]; paint(); applyLive(); }
        };
      });
    };
    // câble CHAQUE panneau d'ajout indépendamment (slot in-grid desktop + add-bar mobile)
    root.querySelectorAll('[data-add-panel]').forEach(panel => {
      const trigger = panel.querySelector('[data-add-trigger]');
      const input = panel.querySelector('[data-add-input]');
      const results = panel.querySelector('[data-add-results]');
      if (!trigger || !input || !results) return;
      trigger.onclick = () => {
        input.hidden = false;
        results.hidden = false;
        input.focus();
        renderResults(results, '');
      };
      input.oninput = () => renderResults(results, input.value);
    });
  }

  // mobile : 2 modèles par défaut (tiennent sans scroll) ; desktop : 3
  const defaultCount = () => (typeof window !== 'undefined' && window.innerWidth <= 780) ? 2 : 3;

  // recharge les best-sellers du segment courant DANS la tranche de budget courante
  async function loadDefaults() {
    const budget = BUDGETS[state.budgetIdx] || {};
    const handles = filterCandidates(state.index, {
      segment: state.segment, budgetMin: budget.min, budgetMax: budget.max,
    }).filter(m => m.best_seller_rank != null)
      .sort((a, b) => a.best_seller_rank - b.best_seller_rank)
      .slice(0, defaultCount())
      .map(m => m.handle);
    state.models = resolveInstant(handles);
    paint();
    applyLive();
  }

  async function loadSegment(seg) {
    state.segment = seg;
    await loadDefaults();
  }

  // wiring statique (segments + budget) — survit aux re-render de la grille.
  // Seulement quand le shell rend la segbar (sinon les éléments n'existent pas).
  if (segments) {
    const segbar = root.querySelector('[data-segbar]');
    if (segbar) segbar.addEventListener('click', e => {
      const chip = e.target.closest('[data-seg]');
      if (chip) loadSegment(chip.dataset.seg);
    });
    const budget = root.querySelector('[data-budget]');
    if (budget) budget.addEventListener('change', e => {
      state.budgetIdx = Number(e.target.value) || 0;
      loadDefaults();   // recharge les best-sellers du segment DANS la tranche choisie
    });
  }

  // index : fourni directement (popup) ou fetch de indexUrl (page)
  if (index) {
    applyIndexLabels(index);
    state.index = Array.isArray(index) ? index : (index.products || []);
  } else {
    let data = [];
    try {
      const r = await fetch(indexUrl, { headers: { Accept: 'application/json' } });
      data = await r.json();
    } catch { data = []; }
    applyIndexLabels(data);
    state.index = Array.isArray(data) ? data : (data && Array.isArray(data.products) ? data.products : []);
  }

  // modèles initiaux
  if (initialHandles && initialHandles.length) {
    state.models = resolveInstant(initialHandles);
    // segment dérivé du 1er modèle si possible (utile en mode page avec ?p=)
    const first = byHandle(initialHandles[0]);
    if (first && (first.segments || []).length) state.segment = first.segments[0];
    paint();
    applyLive();
  } else if (segments) {
    await loadDefaults();
  } else {
    state.models = [];
    paint();
  }

  return {
    setModels: (handles) => { state.models = resolveInstant(handles); paint(); applyLive(); },
    getModels: () => state.models.map(m => m.handle),
    root,
  };
}

export function openComparisonPopup(handles, opts = {}) {
  // singleton : jamais empiler 2 pop-ups (sinon double scroll-lock / fuite au close)
  if (typeof document !== 'undefined' && document.querySelector('.wb-cmp-overlay')) return null;
  const index = opts.index || null;
  const overlay = document.createElement('div');
  overlay.className = 'wb-cmp-overlay';
  overlay.innerHTML = `<div class="wb-cmp-modal" role="dialog" aria-modal="true">
    <div class="wb-cmp-modal-head">
      <span class="wb-cmp-modal-title">${handles.length ? `Comparer ${handles.length} modèle${handles.length > 1 ? 's' : ''}` : 'Comparer les modèles'}</span>
      <button class="wb-cmp-modal-x" aria-label="Fermer">✕</button>
    </div>
    <div class="wb-cmp-modal-body" id="wb-cmp-popup"></div>
  </div>`;
  document.body.appendChild(overlay);
  document.documentElement.classList.add('wb-cmp-noscroll');
  const onKey = e => { if (e.key === 'Escape') close(); };
  function close() {
    overlay.remove();
    document.documentElement.classList.remove('wb-cmp-noscroll');
    document.removeEventListener('keydown', onKey);
    if (opts.onClose) opts.onClose();
  }
  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
  overlay.querySelector('.wb-cmp-modal-x').onclick = close;
  document.addEventListener('keydown', onKey);
  const body = overlay.querySelector('#wb-cmp-popup');
  const view = mountComparison(body, { index, indexUrl: opts.indexUrl, segments: opts.segments || false, initialHandles: (handles && handles.length ? handles : null) });
  return { close, view };
}

export function initComparateur(root) {
  const fromUrl = parsePParam(typeof location !== 'undefined' ? location.search : '');
  return mountComparison(root, {
    indexUrl: root.dataset.index,
    segments: true,
    initialHandles: fromUrl.length ? fromUrl : null,
  });
}

// expose pour le pop-up (réutilise la même vue) — sans clobber un WBCompare préexistant
if (typeof window !== 'undefined') {
  window.WBCompare = Object.assign(window.WBCompare || {}, { mountComparison, openComparisonPopup });
}

// auto-init (inerte hors navigateur, préserve l'usage Node des helpers purs)
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    const root = document.getElementById('wb-cmp');
    if (root) initComparateur(root);
  });
}
